import { readUser } from '../../services/session';
/**
 * GovSolicitationDetailPage — V3
 *
 * Layout matches the residential JobDetailPage:
 *   grid: 1fr 280px  (main content | sticky right rail)
 *
 *   LEFT
 *     Header card (editable KV grid, POC block, quick actions)
 *     Tab pill nav
 *     Tab content
 *
 *   RIGHT RAIL (sticky)
 *     CURRENT STAGE big + progress bar
 *     → Advance Stage button (hard-blocked by server checklist)
 *     ALL STAGES list (colored dots, current highlighted)
 *     Convert to Active / Construction buttons at appropriate stages
 *
 * Tabs (7):
 *   Overview | 📋 Tasks | 💬 Notes | 📄 Documents | 📅 Schedule | 📦 Bid Packet | 💰 Cost Analysis | 🕒 Activity
 *
 * Tasks tab is the PRIMARY workspace — current-stage checklist tasks live here.
 * Overview is a summary view.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import api from '../../services/api';
import GovTaskItem from './GovTaskItem';
import EditableField from './EditableField';
import GovNotesTab from './GovNotesTab';
import GovBidPacketTab from './GovBidPacketTab';
import GovCostAnalysisTab from './GovCostAnalysisTab';
import GovFileUpload from './GovFileUpload';
import GovJobLost from '../../components/GovJobLost';
import {
  tasksFor, DOC_CATEGORY_LABELS, DOC_CATEGORY_ORDER, DOC_CATEGORY_ICONS,
  SOURCES_SOUGHT_TASKS, ACTIVE_TASKS,
} from './govTasks';
import { STAGE_LABELS, STAGE_COLORS, stageOrderFor } from './govStages';

// ─── Types ───────────────────────────────────────────────────────────────────
type Pipeline = 'sources_sought' | 'active';

interface Solicitation {
  id: string;
  pipeline: Pipeline;
  stage: string;
  ss_stage?: string;
  active_stage?: string;
  lost_reason?: string;
  sam_notice_id?: string;
  solicitation_number?: string;
  title: string;
  description?: string;
  opportunity_type?: string;
  naics_code?: string;
  naics_description?: string;
  classification_code?: string;
  set_aside_code?: string;
  set_aside_description?: string;
  agency_name?: string;
  sub_agency_name?: string;
  office_name?: string;
  place_of_performance?: any;
  place_of_performance_display?: string;
  posted_date?: string;
  response_deadline?: string;
  primary_poc_name?: string;
  primary_poc_email?: string;
  primary_poc_phone?: string;
  point_of_contact?: any;
  assigned_to?: string | null;
  bid_captain_id?: string | null;
  go_no_go_decision?: string;
  go_no_go_reason?: string;
  bid_submitted_at?: string;
  bid_submission_method?: string;
  bid_submission_confirmation?: string;
  our_bid_amount?: number;
  award_amount?: number;
  award_date?: string;
  contract_number?: string;
  strategy_notes?: string;
  checklist_state?: Record<string, Record<string, boolean | { checked: boolean; notes?: string }>>;
  task_artifacts?: Record<string, any>;
  sam_ui_link?: string;
  estimated_value?: number;
}

interface Document {
  file_type?: string;
  id: string;
  file_name: string;
  category: string;
  auto_categorized: boolean;
  category_confidence?: string;
  storage_url?: string;
  file_size_bytes?: number;
  source: 'sam_gov' | 'upload';
  uploaded_at: string;
  mime_type?: string;
}

interface Event {
  id: string;
  kind: string;
  title: string;
  description?: string;
  starts_at: string;
  ends_at?: string;
  location?: string;
  source_stage?: string;
  source_task_key?: string;
  auto_generated?: boolean;
}

interface Activity {
  id: string;
  action: string;
  from_stage?: string;
  to_stage?: string;
  message?: string;
  metadata?: any;
  actor_id?: string;
  actor_name?: string;
  created_at: string;
}

interface User { id: string; name?: string; email?: string; }

// ─── Styles (residential-matching) ───────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page: { padding: 32, background: '#0f1923', minHeight: '100vh', color: '#e5edf5' },
  container: { maxWidth: 1400, margin: '0 auto' },
  backBtn: {
    background: 'none', color: '#8fa8c0', fontSize: 14, marginBottom: 20,
    display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
    padding: 0,
  },
  layout: { display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24 },

  // Header card
  headerCard: {
    background: '#1a2b3c', borderRadius: 16, padding: 24,
    border: '1px solid #2d4259', marginBottom: 20,
  },
  headerTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 12, gap: 16, flexWrap: 'wrap' as const,
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const },
  title: { fontSize: 24, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.2 },
  chip: {
    background: '#4a7eb522', color: '#4a7eb5', border: '1px solid #4a7eb544',
    borderRadius: 8, padding: '4px 12px', fontSize: 13, fontWeight: 700,
  },
  chipPurple: {
    background: '#8b5cf622', color: '#a78bfa', border: '1px solid #8b5cf644',
    borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  addressLine: { color: '#8fa8c0', marginBottom: 16, fontSize: 14 },
  kvGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  kvGridWide: { gridColumn: '1 / -1' },
  kvLabel: { color: '#4a6580', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5, fontWeight: 700 },
  kvValue: { color: '#fff', fontSize: 14, fontWeight: 600, marginTop: 2 },
  editBtn: {
    background: '#243447', border: '1px solid #2d4259', color: '#8fa8c0',
    borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  // Tab nav
  tabNav: {
    display: 'flex', gap: 4, marginBottom: 20, background: '#111d27',
    borderRadius: 12, padding: 4, flexWrap: 'nowrap' as const,
  },
  tab: {
    flex: '1 1 auto', minWidth: 0, padding: '9px 8px', borderRadius: 9,
    border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
    transition: 'all 0.15s', whiteSpace: 'nowrap' as const,
    background: 'transparent', color: '#4a6580',
  },
  tabActive: { background: '#4a7eb5', color: '#fff' },

  // Right rail
  rail: {
    background: '#1a2b3c', borderRadius: 16, padding: 20,
    border: '1px solid #2d4259', position: 'sticky' as const, top: 20,
    height: 'fit-content' as const,
  },
  railHeader: { fontSize: 11, color: '#4a6580', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 4 },
  railStage: { fontSize: 18, fontWeight: 800, marginBottom: 4 },
  railStageNum: { fontSize: 12, color: '#8fa8c0', marginBottom: 10 },
  railProgressBar: { background: '#243447', borderRadius: 4, height: 8, overflow: 'hidden' as const, marginBottom: 16 },
  railProgressFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s' },
  advanceBtn: {
    width: '100%', padding: '12px', background: '#10b981', color: '#fff',
    border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14,
    cursor: 'pointer', marginBottom: 8,
  },
  advanceBtnDisabled: {
    width: '100%', padding: '12px', background: '#334155', color: '#64748b',
    border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14,
    cursor: 'not-allowed', marginBottom: 8,
  },
  convertBtn: {
    width: '100%', padding: '12px', background: '#8b5cf6', color: '#fff',
    border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14,
    cursor: 'pointer', marginBottom: 8,
  },
  railStageList: { marginTop: 16 },
  railStageRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' },
  railDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  railStageName: { fontSize: 12 },

  // Common
  card: { background: '#1a2b3c', borderRadius: 12, padding: 20, border: '1px solid #2d4259', marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#fff', margin: 0, marginBottom: 12 },
  errBanner: {
    padding: 12, background: '#7f1d1d', color: '#fecaca', borderRadius: 8,
    fontSize: 13, marginBottom: 12,
  },
  loading: { padding: 60, textAlign: 'center' as const, color: '#8fa8c0' },
  input: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 6,
    padding: '8px 10px', color: '#e5edf5', fontSize: 13, width: '100%',
    boxSizing: 'border-box' as const,
  },
  ghostBtn: {
    background: 'transparent', color: '#8fa8c0', border: '1px solid #2d4259',
    borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
  },
  primaryBtn: {
    background: '#4a7eb5', color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },

  // Documents tab
  docsGroup: { marginBottom: 20 },
  docsGroupTitle: {
    fontSize: 13, fontWeight: 700, color: '#e5edf5', marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 8,
  },
  docRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    background: '#243447', borderRadius: 6, marginBottom: 6, fontSize: 13,
  },

  // Schedule tab
  eventRow: {
    display: 'flex', gap: 12, padding: 12, background: '#243447', borderRadius: 8,
    marginBottom: 8, alignItems: 'center',
  },
  deadlineChip: { padding: '4px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, color: '#fff' },

  // Activity tab
  activityRow: {
    display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #243447',
    fontSize: 13, alignItems: 'flex-start',
  },
  labelSm: { fontSize: 10, color: '#8fa8c0', textTransform: 'uppercase' as const, letterSpacing: 0.6, marginBottom: 6, fontWeight: 700 },
  k: { fontSize: 10, color: '#8fa8c0', textTransform: 'uppercase' as const, letterSpacing: 0.6, marginBottom: 3, fontWeight: 700 },
  v: { fontSize: 13, color: '#e5edf5', lineHeight: 1.4, wordBreak: 'break-word' as const },
  kv: { marginBottom: 12 },
  select: {
    padding: '8px 12px', borderRadius: 6, background: '#243447',
    border: '1px solid #2d4259', color: '#e5edf5', fontSize: 13, width: '100%',
    boxSizing: 'border-box' as const,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────
const GovSolicitationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [sol, setSol] = useState<Solicitation | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<
    'overview' | 'tasks' | 'notes' | 'documents' | 'schedule' | 'bid_packet' | 'cost_analysis' | 'activity'
  >('overview');
  const [advanceError, setAdvanceError] = useState<string[] | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const currentUserId = (() => {
    try { return readUser()?.id; } catch { return undefined; }
  })();
  const isAdmin = (() => {
    try { return readUser()?.role === 'admin'; } catch { return false; }
  })();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setError(null);
      const [detailRes, evRes, usersRes] = await Promise.all([
        api.get(`/gov/solicitations/${id}`),
        api.get(`/gov/solicitations/${id}/events`).catch(() => ({ data: { events: [] } })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      const detail: any = detailRes.data;
      const record: Solicitation = detail.solicitation || detail;
      if (!record?.id) throw new Error('Malformed response — no solicitation record');
      setSol(record);
      setDocuments(detail.documents || []);
      setActivity(detail.activity || []);
      setEvents(evRes.data?.events || []);
      const rawUsers: any = usersRes.data;
      setUsers(Array.isArray(rawUsers) ? rawUsers : (rawUsers?.users || []));
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const reloadDocuments = async () => {
    const { data } = await api.get(`/gov/solicitations/${id}/documents`);
    setDocuments(data.documents || []);
  };

  // ─── Derived ──────────────────────────────────────────────────────────────
  const stageOrder = sol ? stageOrderFor(sol.pipeline) : [];
  const currentStage = sol?.stage || 'identified';
  const stageIdx = stageOrder.indexOf(currentStage);
  const nextStage = !['won', 'lost', 'no_bid', 'cancelled'].includes(currentStage) && stageIdx >= 0 && stageIdx < stageOrder.length - 1 ? stageOrder[stageIdx + 1] : null;
  const stageColor = STAGE_COLORS[currentStage] || '#4a7eb5';
  const progressPct = stageOrder.length > 0 ? Math.round(((stageIdx + 1) / stageOrder.length) * 100) : 0;

  const tasks = useMemo(
    () => (sol ? tasksFor(sol.pipeline, currentStage) : []),
    [sol, currentStage]
  );
  const checklistState = sol?.checklist_state?.[currentStage] || {};

  const isChecked = (key: string): boolean => {
    const v = checklistState[key];
    return typeof v === 'object' ? !!v?.checked : !!v;
  };
  const checkedCount = tasks.filter((t) => isChecked(t.key)).length;
  const stageComplete = tasks.length === 0 || checkedCount === tasks.length;

  const deadlineDaysLeft = sol?.response_deadline
    ? differenceInCalendarDays(parseISO(sol.response_deadline), new Date())
    : null;
  const deadlineColor = deadlineDaysLeft == null
    ? '#526980'
    : deadlineDaysLeft < 3 ? '#ef4444'
    : deadlineDaysLeft < 7 ? '#f97316'
    : deadlineDaysLeft < 30 ? '#eab308'
    : '#4a7eb5';

  const userName = (uid?: string | null) => {
    if (!uid) return null;
    const u = users.find((x) => x.id === uid);
    return u?.name || u?.email || null;
  };

  // ─── Actions ──────────────────────────────────────────────────────────────
  const toggleCheck = async (key: string, checked: boolean) => {
    if (!id || !sol) return;
    setSol((prev) => {
      if (!prev) return prev;
      const cs = { ...(prev.checklist_state || {}) };
      cs[currentStage] = { ...(cs[currentStage] || {}), [key]: checked };
      return { ...prev, checklist_state: cs };
    });
    try {
      await api.patch(`/gov/solicitations/${id}/checklist`, { stage: currentStage, key, checked });
    } catch (e) { load(); }
  };

  const patchArtifact = async (stage: string, task_key: string, artifact: any) => {
    if (!id) return;
    setSol((prev) => {
      if (!prev) return prev;
      const a = { ...(prev.task_artifacts || {}) };
      const k = `${stage}.${task_key}`;
      a[k] = { ...(a[k] || {}), ...artifact };
      return { ...prev, task_artifacts: a };
    });
    try {
      await api.patch(`/gov/solicitations/${id}/task-artifact`, { stage, task_key, artifact });
    } catch (e) { /* soft */ }
  };

  const patchSolicitation = async (patch: Record<string, any>) => {
    if (!id) return;
    setSol((prev) => (prev ? { ...prev, ...patch } : prev));
    try {
      const { data } = await api.patch(`/gov/solicitations/${id}`, patch);
      const updated: Solicitation = data?.solicitation || data;
      if (updated?.id) setSol(updated);
    } catch (e) { /* soft */ }
  };

  const addEvent = async (event: any) => {
    if (!id) return;
    try {
      await api.post(`/gov/solicitations/${id}/events`, event);
      const evRes = await api.get(`/gov/solicitations/${id}/events`);
      setEvents(evRes.data?.events || []);
    } catch (e: any) {
      alert(`Failed to create event: ${e?.response?.data?.error || e.message}`);
    }
  };

  const advance = async () => {
    if (!id || !nextStage) return;
    setAdvanceError(null);
    try {
      await api.post(`/gov/solicitations/${id}/advance`, { to_stage: nextStage });
      load();
    } catch (e: any) {
      const resp = e?.response?.data;
      if (resp?.checklist_incomplete) {
        setAdvanceError(resp.unmet_items || []);
        setTab('tasks'); // jump to tasks so user can see what's missing
      } else {
        alert(`Advance failed: ${resp?.error || e.message}`);
      }
    }
  };

  const forceStage = async (to: string) => {
    if (!id) return;
    if (!isAdmin) { alert('Only admins can jump stages.'); return; }
    try {
      await api.post(`/gov/solicitations/${id}/advance?force=true`, { to_stage: to });
      load();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const deleteSolicitation = async () => {
    if (!id || !sol || deleting) return;
    if (!window.confirm(
      `Permanently delete "${sol.title}" from the CRM?\n\nIts checklist, notes, and linked solicitation records will also be removed. This cannot be undone.`
    )) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/gov/solicitations/${id}`);
      nav('/gov/solicitations', { replace: true });
    } catch (e: any) {
      setDeleteError(e?.response?.data?.error || 'Could not delete the solicitation. Please try again.');
      setDeleting(false);
    }
  };

  const convert = async (to_pipeline: 'active' | 'construction') => {
    if (!id) return;
    try {
      const { data } = await api.post(`/gov/solicitations/${id}/convert`, { to_pipeline });
      if (to_pipeline === 'active' && data?.solicitation?.id) {
        nav(`/gov/solicitations/${data.solicitation.id}`);
      } else if (to_pipeline === 'construction' && data?.construction_job?.id) {
        nav(`/gov/construction/${data.construction_job.id}`);
      }
    } catch (e: any) {
      alert(`Convert failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  if (loading) return <div style={S.page}><div style={S.loading}>Loading solicitation…</div></div>;
  if (error) return <div style={S.page}><div style={S.errBanner}>Error: {error}</div></div>;
  if (!sol) return <div style={S.page}><div style={S.loading}>Not found</div></div>;

  // ─── Render ───────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',      label: '📋 Overview' },
    { id: 'tasks',         label: `✅ Tasks (${checkedCount}/${tasks.length})` },
    { id: 'notes',         label: '💬 Notes' },
    { id: 'documents',     label: `📄 Documents (${documents.length})` },
    { id: 'schedule',      label: `📅 Schedule (${events.length})` },
    { id: 'bid_packet',    label: '📦 Bid Packet' },
    { id: 'cost_analysis', label: '💰 Cost Analysis' },
    { id: 'activity',      label: '🕒 Activity' },
  ] as const;

  return (
    <div style={S.page}>
      <div style={S.container}>
        <button onClick={() => nav('/gov/solicitations')} style={S.backBtn}>
          ← Back to Solicitations
        </button>

        <div style={S.layout}>
          {/* ─── LEFT COLUMN ─── */}
          <div>
            {/* Header card */}
            <div style={S.headerCard}>
              <div style={S.headerTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.titleRow}>
                    <h1 style={S.title}>{sol.title}</h1>
                    {/* Solicitation # now lives in the KV grid; pill removed to avoid dupe */}
                  </div>
                  <div style={{ ...S.addressLine, marginTop: 8 }}>
                    🏛️ {sol.agency_name || 'Agency TBD'}
                    {sol.sub_agency_name && <span style={{ opacity: 0.7 }}> · {sol.sub_agency_name}</span>}
                    {sol.office_name && <span style={{ opacity: 0.7 }}> · {sol.office_name}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
                  <span style={S.chipPurple}>
                    {sol.pipeline === 'sources_sought' ? 'Sources Sought / RFI' : 'Active Solicitation'}
                  </span>
                  <button style={S.editBtn} onClick={() => setEditMode((m) => !m)}>
                    {editMode ? '✓ Done Editing' : '✏️ Edit'}
                  </button>
                  <button
                    style={{ ...S.editBtn, color: '#fca5a5', borderColor: '#ef444466', opacity: deleting ? 0.6 : 1 }}
                    onClick={deleteSolicitation}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete solicitation'}
                  </button>
                </div>
              </div>

              {deleteError && <div role="alert" style={S.errBanner}>{deleteError}</div>}

              {/* KV grid — read mode */}
              {!editMode && (
                <div style={S.kvGrid}>
                  <div>
                    <div style={S.kvLabel}>SOLICITATION #</div>
                    <div style={{ ...S.kvValue, fontFamily: 'monospace' }}>
                      {sol.solicitation_number || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>NAICS</div>
                    <div style={S.kvValue}>
                      {sol.naics_code || '—'}
                      {sol.naics_description && (
                        <div style={{ color: '#8fa8c0', fontSize: 11, fontWeight: 400 }}>
                          {sol.naics_description}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>SET-ASIDE</div>
                    <div style={S.kvValue}>{sol.set_aside_description || sol.set_aside_code || '—'}</div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>TYPE</div>
                    <div style={S.kvValue}>{sol.opportunity_type || '—'}</div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>PSC</div>
                    <div style={S.kvValue}>{sol.classification_code || '—'}</div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>PLACE OF PERF</div>
                    <div style={S.kvValue}>{sol.place_of_performance_display || '—'}</div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>POSTED</div>
                    <div style={S.kvValue}>
                      {sol.posted_date ? format(parseISO(sol.posted_date), 'MMM d, yyyy') : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={S.kvLabel}>DEADLINE</div>
                    <div style={{ ...S.kvValue, color: deadlineColor }}>
                      {sol.response_deadline ? format(parseISO(sol.response_deadline), 'MMM d · h:mm a') : '—'}
                      {deadlineDaysLeft != null && (
                        <span style={{ marginLeft: 6, fontSize: 11 }}>
                          ({deadlineDaysLeft <= 0 ? 'PAST' : `${deadlineDaysLeft}d`})
                        </span>
                      )}
                    </div>
                  </div>
                  {sol.estimated_value && (
                    <div>
                      <div style={S.kvLabel}>EST. VALUE</div>
                      <div style={{ ...S.kvValue, color: '#10b981' }}>
                        ${sol.estimated_value.toLocaleString()}
                      </div>
                    </div>
                  )}
                  {/* POC block spans full width */}
                  <div style={S.kvGridWide}>
                    <div style={{ ...S.kvLabel, marginTop: 8 }}>POINT OF CONTACT</div>
                    {sol.primary_poc_name ? (
                      <div style={{ display: 'flex', gap: 20, marginTop: 4, flexWrap: 'wrap' as const }}>
                        <div>
                          <div style={{ fontSize: 14, color: '#fff', fontWeight: 700 }}>{sol.primary_poc_name}</div>
                        </div>
                        {sol.primary_poc_email && (
                          <a href={`mailto:${sol.primary_poc_email}`} style={{ color: '#4a7eb5', fontWeight: 600 }}>
                            📧 {sol.primary_poc_email}
                          </a>
                        )}
                        {sol.primary_poc_phone && (
                          <a href={`tel:${sol.primary_poc_phone}`} style={{ color: '#4a7eb5', fontWeight: 600 }}>
                            📞 {sol.primary_poc_phone}
                          </a>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: '#526980', fontStyle: 'italic', fontSize: 13, marginTop: 4 }}>
                        No POC recorded. {sol.sam_notice_id && 'Try Re-fetch metadata below.'}
                      </div>
                    )}
                  </div>
                  {/* Assignment row */}
                  <div style={{ ...S.kvGridWide, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 8 }}>
                    <div>
                      <div style={S.kvLabel}>ASSIGNED BIDDER</div>
                      <select
                        style={S.select}
                        value={sol.assigned_to || ''}
                        onChange={(e) => patchSolicitation({ assigned_to: e.target.value || null })}
                      >
                        <option value="">— Unassigned —</option>
                        {users.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
                      </select>
                    </div>
                    <div>
                      <div style={S.kvLabel}>BID CAPTAIN</div>
                      <select
                        style={S.select}
                        value={sol.bid_captain_id || ''}
                        onChange={(e) => patchSolicitation({ bid_captain_id: e.target.value || null })}
                      >
                        <option value="">— Unassigned —</option>
                        {users.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
                      </select>
                    </div>
                  </div>
                  {sol.sam_ui_link && (
                    <div style={S.kvGridWide}>
                      <a href={sol.sam_ui_link} target="_blank" rel="noreferrer"
                        style={{ color: '#4a7eb5', fontSize: 13, fontWeight: 700 }}>
                        🔗 Open on SAM.gov
                      </a>
                      {sol.sam_notice_id && (
                        <button
                          style={{ ...S.ghostBtn, marginLeft: 12, fontSize: 11 }}
                          onClick={async () => {
                            if (!window.confirm('Re-fetch metadata from SAM.gov? Only fields SAM returns will be updated; your edits are preserved.')) return;
                            try {
                              await api.post(`/gov/solicitations/${sol.id}/refetch-metadata`);
                              load();
                            } catch (e: any) {
                              alert(`Failed: ${e?.response?.data?.error || e.message}`);
                            }
                          }}
                        >📡 Re-fetch metadata</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Edit mode — inline editable */}
              {editMode && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                  <EditableField label="Title" value={sol.title} type="text"
                    onSave={(v) => patchSolicitation({ title: v })} />
                  <EditableField label="Solicitation #" value={sol.solicitation_number} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ solicitation_number: v })} />
                  <EditableField label="Agency" value={sol.agency_name} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ agency_name: v })} />
                  <EditableField label="Sub-Agency" value={sol.sub_agency_name} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ sub_agency_name: v })} />
                  <EditableField label="Office" value={sol.office_name} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ office_name: v })} />
                  <EditableField label="NAICS Code" value={sol.naics_code} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ naics_code: v })} />
                  <EditableField label="PSC" value={sol.classification_code} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ classification_code: v })} />
                  <EditableField label="Set-Aside" value={sol.set_aside_description || sol.set_aside_code} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ set_aside_description: v })} />
                  <EditableField label="Place of Performance" value={sol.place_of_performance_display} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ place_of_performance_display: v })} />
                  <EditableField label="Type" value={sol.opportunity_type} type="text"
                    onSave={(v) => patchSolicitation({ opportunity_type: v })} />
                  <EditableField label="Posted Date" value={sol.posted_date} type="date"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ posted_date: v })} />
                  <EditableField label="Response Deadline" value={sol.response_deadline} type="datetime"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ response_deadline: v })} />
                  <EditableField label="Estimated Value" value={sol.estimated_value} type="number" isMoney
                    onSave={(v) => patchSolicitation({ estimated_value: v })} />
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ borderTop: '1px solid #2d4259', paddingTop: 12, marginTop: 4 }}>
                      <div style={{ ...S.labelSm, marginBottom: 10 }}>👤 POINT OF CONTACT</div>
                    </div>
                  </div>
                  <EditableField label="POC Name" value={sol.primary_poc_name} type="text"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ primary_poc_name: v })} />
                  <EditableField label="POC Email" value={sol.primary_poc_email} type="email"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ primary_poc_email: v })} />
                  <EditableField label="POC Phone" value={sol.primary_poc_phone} type="phone"
                    fromSam={!!sol.sam_notice_id}
                    onSave={(v) => patchSolicitation({ primary_poc_phone: v })} />
                </div>
              )}
            </div>

            {/* Tab pill nav */}
            <div style={S.tabNav}>
              {TABS.map((t) => (
                <button key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }}
                >{t.label}</button>
              ))}
            </div>

            {/* Checklist-incomplete banner (only shows if user just tried to advance) */}
            {advanceError && (
              <div style={S.errBanner}>
                <b>Cannot advance — checklist incomplete.</b> Missing:
                <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                  {advanceError.map((k) => {
                    const t = tasks.find((x) => x.key === k);
                    return <li key={k}>{t?.label || k}</li>;
                  })}
                </ul>
              </div>
            )}

            {/* Tab content */}
            {tab === 'overview' && (
              <OverviewTab
                sol={sol}
                tasks={tasks}
                currentStage={currentStage}
                checkedCount={checkedCount}
                stageComplete={stageComplete}
                nextStage={nextStage}
                events={events}
                activity={activity}
                documents={documents}
                userName={userName}
                onAdvance={advance}
                onGoToTasks={() => setTab('tasks')}
                onGoToSchedule={() => setTab('schedule')}
                onGoToDocs={() => setTab('documents')}
              />
            )}

            {tab === 'tasks' && (
              <TasksTab
                sol={sol}
                tasks={tasks}
                currentStage={currentStage}
                checkedCount={checkedCount}
                stageComplete={stageComplete}
                nextStage={nextStage}
                checklistState={checklistState}
                isChecked={isChecked}
                documents={documents}
                users={users}
                onToggleCheck={toggleCheck}
                onArtifactChange={patchArtifact}
                onSolicitationChange={patchSolicitation}
                onAddEvent={addEvent}
                onOpenDocsTab={() => setTab('documents')}
                onAdvance={advance}
              />
            )}

            {tab === 'notes' && (
              <GovNotesTab
                solicitationId={sol.id}
                users={users}
                currentUserId={currentUserId}
              />
            )}

            {tab === 'documents' && (
              <DocumentsTab
                solicitationId={sol.id}
                documents={documents}
                onReload={reloadDocuments}
              />
            )}

            {tab === 'schedule' && (
              <ScheduleTab
                solicitationId={sol.id}
                events={events}
                onReload={load}
                hasResponseDeadline={!!sol.response_deadline}
              />
            )}

            {tab === 'bid_packet' && (
              <GovBidPacketTab solicitationId={sol.id} />
            )}

            {tab === 'cost_analysis' && (
              <GovCostAnalysisTab solicitationId={sol.id} onDocumentsChanged={reloadDocuments} />
            )}

            {tab === 'activity' && (
              <ActivityTab activity={activity} />
            )}
          </div>

          {/* ─── RIGHT RAIL ─── */}
          <div>
            <div style={{ ...S.rail, borderColor: `${stageColor}44` }}>
              <div style={S.railHeader}>CURRENT STAGE</div>
              <div style={{ ...S.railStage, color: stageColor }}>
                {STAGE_LABELS[currentStage] || currentStage}
              </div>
              <div style={S.railStageNum}>
                Stage {stageIdx + 1} of {stageOrder.length}
              </div>
              <div style={S.railProgressBar}>
                <div style={{ ...S.railProgressFill, background: stageColor, width: `${progressPct}%` }} />
              </div>

              {/* Advance button */}
              {nextStage && (
                <button
                  style={stageComplete ? S.advanceBtn : S.advanceBtnDisabled}
                  disabled={!stageComplete}
                  onClick={advance}
                  title={stageComplete ? '' : 'Complete all checklist tasks first'}
                >
                  → Advance to {STAGE_LABELS[nextStage]}
                </button>
              )}

              {/* Task progress under advance button */}
              {tasks.length > 0 && (
                <div style={{ fontSize: 11, color: '#8fa8c0', textAlign: 'center' as const, marginBottom: 12 }}>
                  {checkedCount} of {tasks.length} tasks complete
                </div>
              )}

              {/* Convert buttons */}
              {sol.pipeline === 'sources_sought' && currentStage === 'solicitation_released' && (
                <button style={S.convertBtn} onClick={() => convert('active')}>
                  🔄 Convert to Active
                </button>
              )}
              {sol.pipeline === 'active' && currentStage === 'won' && (
                <button style={{ ...S.convertBtn, background: '#4a7eb5' }} onClick={() => convert('construction')}>
                  🏗️ Convert to Construction
                </button>
              )}

              {/* Admin stage jump */}
              {isAdmin && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...S.labelSm, fontSize: 9, marginBottom: 4 }}>Admin: Jump to Stage</div>
                  <select
                    style={S.select}
                    value={currentStage}
                    onChange={(e) => forceStage(e.target.value)}
                  >
                    {stageOrder.map((st) => (
                      <option key={st} value={st}>{STAGE_LABELS[st] || st}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* All stages list */}
              <div style={S.railStageList}>
                <div style={{ ...S.railHeader, marginBottom: 8 }}>ALL STAGES</div>
                {stageOrder.map((s, i) => (
                  <div key={s} style={S.railStageRow}>
                    <div style={{
                      ...S.railDot,
                      background: i < stageIdx ? '#10b981' : i === stageIdx ? stageColor : '#2d4259',
                    }} />
                    <span style={{
                      ...S.railStageName,
                      color: i === stageIdx ? stageColor : i < stageIdx ? '#10b981' : '#4a6580',
                      fontWeight: i === stageIdx ? 700 : 400,
                    }}>
                      {STAGE_LABELS[s] || s}
                    </span>
                  </div>
                ))}
              </div>

              {sol.pipeline === 'active' && !['won', 'lost', 'no_bid', 'cancelled'].includes(currentStage) && (
                <GovJobLost job={{ id: sol.id, stage: currentStage }} onSaved={load} />
              )}
              {currentStage === 'lost' && sol.lost_reason && (
                <p style={{ color: '#8fa8c0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{sol.lost_reason}</p>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── OVERVIEW TAB — summary view ─────────────────────────────────────────────
const OverviewTab: React.FC<{
  sol: Solicitation;
  tasks: any[];
  currentStage: string;
  checkedCount: number;
  stageComplete: boolean;
  nextStage: string | null;
  events: Event[];
  activity: Activity[];
  documents: Document[];
  userName: (uid?: string | null) => string | null;
  onAdvance: () => void;
  onGoToTasks: () => void;
  onGoToSchedule: () => void;
  onGoToDocs: () => void;
}> = ({ sol, tasks, currentStage, checkedCount, stageComplete, nextStage, events, activity, documents, userName, onAdvance, onGoToTasks, onGoToSchedule, onGoToDocs }) => {
  const upcomingEvents = events.filter((e) => new Date(e.starts_at) >= new Date()).slice(0, 3);
  const recentActivity = activity.slice(0, 5);
  const overallProgress = tasks.length === 0 ? 100 : Math.round((checkedCount / tasks.length) * 100);
  const now = new Date();

  return (
    <>
      {/* Task progress card — CTA to Tasks tab */}
      <div style={{ ...S.card, borderColor: stageComplete ? '#10b98155' : '#2d4259' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div>
            <div style={S.cardTitle}>Current Stage Tasks</div>
            <div style={{ fontSize: 13, color: '#8fa8c0' }}>
              {STAGE_LABELS[currentStage] || currentStage} · {checkedCount} of {tasks.length} tasks complete
            </div>
          </div>
          <button style={S.primaryBtn} onClick={onGoToTasks}>Open Tasks →</button>
        </div>
        <div style={{ background: '#243447', borderRadius: 4, height: 6, overflow: 'hidden' }}>
          <div style={{
            width: `${overallProgress}%`, height: '100%',
            background: stageComplete ? '#10b981' : '#4a7eb5',
            transition: 'width 0.3s',
          }} />
        </div>
        {stageComplete && nextStage && (
          <button
            style={{ ...S.primaryBtn, background: '#10b981', width: '100%', marginTop: 12, padding: 10 }}
            onClick={onAdvance}
          >
            ✓ All tasks complete — Advance to {STAGE_LABELS[nextStage]}
          </button>
        )}
      </div>

      {/* Quick stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.k}>📄 Documents</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', marginTop: 4 }}>{documents.length}</div>
          <button style={{ ...S.ghostBtn, marginTop: 6, fontSize: 11 }} onClick={onGoToDocs}>View →</button>
        </div>
        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.k}>📅 Upcoming Events</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', marginTop: 4 }}>{upcomingEvents.length}</div>
          <button style={{ ...S.ghostBtn, marginTop: 6, fontSize: 11 }} onClick={onGoToSchedule}>View →</button>
        </div>
        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.k}>👥 Team</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 4 }}>
            {userName(sol.assigned_to) || 'Unassigned'}
          </div>
          <div style={{ fontSize: 11, color: '#8fa8c0', marginTop: 2 }}>
            {sol.bid_captain_id ? `Captain: ${userName(sol.bid_captain_id)}` : 'No captain assigned'}
          </div>
        </div>
      </div>

      {/* Upcoming events */}
      {upcomingEvents.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>📅 Upcoming Events</div>
          {upcomingEvents.map((ev) => {
            const days = differenceInCalendarDays(parseISO(ev.starts_at), now);
            return (
              <div key={ev.id} style={S.eventRow}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{ev.title}</div>
                  <div style={{ fontSize: 12, color: '#8fa8c0' }}>
                    {format(parseISO(ev.starts_at), 'EEE MMM d · h:mm a')}
                  </div>
                </div>
                <div style={{ ...S.deadlineChip, background: days < 3 ? '#ef4444' : days < 7 ? '#f97316' : '#4a7eb5' }}>
                  {days === 0 ? 'TODAY' : `${days}d`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent activity preview */}
      {recentActivity.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>🕒 Recent Activity</div>
          {recentActivity.map((a) => (
            <div key={a.id} style={S.activityRow}>
              <div style={{ minWidth: 100, color: '#8fa8c0', fontSize: 11 }}>
                {format(parseISO(a.created_at), 'MMM d h:mm a')}
              </div>
              <div style={{ flex: 1, fontSize: 12 }}>
                <span style={{ color: '#e5edf5', fontWeight: 600 }}>{a.action.replace(/_/g, ' ')}</span>
                {a.actor_name && <span style={{ color: '#8fa8c0' }}> by {a.actor_name}</span>}
                {a.message && <div style={{ color: '#8fa8c0', marginTop: 2 }}>{a.message.slice(0, 100)}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Description */}
      {sol.description && (
        <div style={S.card}>
          <div style={S.cardTitle}>📄 Description</div>
          <div style={{ fontSize: 13, color: '#e5edf5', lineHeight: 1.6, whiteSpace: 'pre-wrap' as const }}>
            {sol.description.length > 800 ? sol.description.slice(0, 800) + '…' : sol.description}
          </div>
        </div>
      )}
    </>
  );
};

// ─── TASKS TAB — the primary workspace ───────────────────────────────────────
const TasksTab: React.FC<{
  sol: Solicitation;
  tasks: any[];
  currentStage: string;
  checkedCount: number;
  stageComplete: boolean;
  nextStage: string | null;
  checklistState: Record<string, any>;
  isChecked: (key: string) => boolean;
  documents: Document[];
  users: User[];
  onToggleCheck: (key: string, checked: boolean) => void;
  onArtifactChange: (stage: string, key: string, artifact: any) => void;
  onSolicitationChange: (patch: Record<string, any>) => void;
  onAddEvent: (e: any) => void;
  onOpenDocsTab: () => void;
  onAdvance: () => void;
}> = ({ sol, tasks, currentStage, checkedCount, stageComplete, nextStage, isChecked, documents, users,
        onToggleCheck, onArtifactChange, onSolicitationChange, onAddEvent, onOpenDocsTab, onAdvance }) => {
  const progressPct = tasks.length === 0 ? 100 : Math.round((checkedCount / tasks.length) * 100);
  const stageColor = STAGE_COLORS[currentStage] || '#4a7eb5';

  return (
    <>
      {/* Stage banner */}
      <div style={{ ...S.card, borderColor: `${stageColor}66` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: '#8fa8c0', textTransform: 'uppercase' as const, letterSpacing: 0.5, fontWeight: 700 }}>Current Stage</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: stageColor, marginTop: 2 }}>
              {STAGE_LABELS[currentStage] || currentStage}
            </div>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#e5edf5' }}>
              {checkedCount}<span style={{ color: '#8fa8c0', fontSize: 20 }}> / {tasks.length}</span>
            </div>
            <div style={{ fontSize: 11, color: '#8fa8c0' }}>tasks complete</div>
          </div>
        </div>
        <div style={{ background: '#243447', borderRadius: 4, height: 8, overflow: 'hidden' }}>
          <div style={{
            width: `${progressPct}%`, height: '100%', background: stageComplete ? '#10b981' : stageColor,
            transition: 'width 0.3s',
          }} />
        </div>
      </div>

      {tasks.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center' as const, color: '#8fa8c0', padding: 40 }}>
          {['no_response_needed', 'not_converted', 'converted', 'lost', 'no_bid', 'cancelled', 'won'].includes(currentStage)
            ? 'This is a terminal stage — no tasks required.'
            : 'No tasks defined for this stage yet.'}
        </div>
      ) : (
        <div>
          {tasks.map((t) => {
            const artifactKey = `${currentStage}.${t.key}`;
            const artifact = sol.task_artifacts?.[artifactKey];
            return (
              <GovTaskItem
                key={t.key}
                solicitationId={sol.id}
                stage={currentStage}
                task={t}
                checked={isChecked(t.key)}
                artifact={artifact}
                solicitation={sol}
                documents={documents}
                users={users}
                onToggle={(checked) => onToggleCheck(t.key, checked)}
                onArtifactChange={(a) => onArtifactChange(currentStage, t.key, a)}
                onSolicitationChange={onSolicitationChange}
                onAddEvent={onAddEvent}
                onOpenDocsTab={onOpenDocsTab}
              />
            );
          })}
        </div>
      )}

      {/* Advance button at bottom (contextual) */}
      {nextStage && (
        <div style={{ ...S.card, marginTop: 8, textAlign: 'center' as const, background: stageComplete ? '#0f3628' : '#1a2b3c' }}>
          {stageComplete ? (
            <>
              <div style={{ fontSize: 14, color: '#10b981', fontWeight: 700, marginBottom: 10 }}>
                🎉 All tasks complete for this stage
              </div>
              <button
                style={{ ...S.primaryBtn, background: '#10b981', padding: '12px 32px', fontSize: 14 }}
                onClick={onAdvance}
              >
                → Advance to {STAGE_LABELS[nextStage]}
              </button>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#8fa8c0' }}>
              Complete all {tasks.length} tasks to advance to <b>{STAGE_LABELS[nextStage]}</b>
            </div>
          )}
        </div>
      )}
    </>
  );
};

// ─── DOCUMENTS TAB ───────────────────────────────────────────────────────────
export const DocumentsTab: React.FC<{
  solicitationId: string;
  documents: Document[];
  onReload: () => void;
}> = ({ solicitationId, documents, onReload }) => {
  const [refetching, setRefetching] = useState(false);
  const [externalLinks, setExternalLinks] = useState<{ name: string | null; url: string }[]>([]);
  const [resourceError, setResourceError] = useState('');
  React.useEffect(() => {
    let active = true;
    setExternalLinks([]);
    setResourceError('');
    api.get(`/gov/solicitations/${solicitationId}/sam-resources`)
      .then(({ data }) => { if (active) setExternalLinks(data.external_links || []); })
      .catch((e) => { if (active) setResourceError(e?.response?.data?.error || 'Unable to check SAM.gov resource links. Retry the fetch.'); });
    return () => { active = false; };
  }, [solicitationId]);

  const grouped: Record<string, Document[]> = {};
  for (const cat of DOC_CATEGORY_ORDER) grouped[cat] = [];
  documents.forEach((d) => {
    const cat = d.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  const handleRecategorize = async (docId: string, category: string) => {
    try {
      await api.patch(`/gov/solicitations/documents/${docId}`, { category });
      onReload();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/gov/solicitations/documents/${docId}`);
      onReload();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const reclassifyAll = async () => {
    try {
      const { data } = await api.post(`/gov/solicitations/${solicitationId}/reclassify-documents`);
      onReload();
      alert(`Reclassified ${data.reclassified} of ${data.total} documents.`);
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const refetchFromSam = async () => {
    setRefetching(true);
    try {
      const { data } = await api.post(`/gov/solicitations/${solicitationId}/refetch-sam-docs`);
      onReload();
      setExternalLinks(data.external_links || []);
      setResourceError('');
      const summary = [
        `${data.downloaded || 0} file(s) downloaded.`,
        data.external_links?.length ? `${data.external_links.length} external link(s) listed below. Open the source portal for its bid documents.` : '',
        data.already_had || data.skipped_duplicate ? `${(data.already_had || 0) + (data.skipped_duplicate || 0)} file(s) already present.` : '',
        data.failed ? `${data.failed} file(s) failed to download. Please retry or obtain them from the source portal.` : '',
      ].filter(Boolean).join(' ');
      alert(data.message || summary);
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setRefetching(false);
    }
  };

  const fmtSize = (b?: number) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <GovFileUpload solicitationId={solicitationId} onUploaded={onReload} />
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' as const }}>
        <button style={{ ...S.primaryBtn, background: '#4a7eb5' }} onClick={refetchFromSam} disabled={refetching}>
          {refetching ? 'Fetching…' : '📡 Re-fetch from SAM.gov'}
        </button>
        <button style={S.ghostBtn} onClick={reclassifyAll}>
          🔄 Re-classify auto-tagged
        </button>
        <div style={{ marginLeft: 'auto', color: '#8fa8c0', fontSize: 12 }}>
          {documents.length} total · {documents.filter((d) => d.source === 'sam_gov').length} from SAM.gov
        </div>
      </div>

      {resourceError && <div role="alert" style={{ ...S.card, color: '#fbbf24' }}>{resourceError}</div>}
      {externalLinks.length > 0 && (
        <div style={S.card}>
          <h3 style={{ marginTop: 0 }}>External resources listed on SAM.gov ({externalLinks.length})</h3>
          <p style={{ color: '#8fa8c0' }}>These are source links. Files hosted on another portal, such as PIEE, must be obtained there and uploaded here.</p>
          {externalLinks.map((link) => (
            <div key={link.url} style={{ marginBottom: 10 }}>
              <a href={link.url} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>{link.name || link.url} ↗</a>
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && (
        <div style={{ ...S.card, textAlign: 'center' as const, color: '#8fa8c0' }}>
          No documents yet. Upload the solicitation packet or fetch from SAM.gov.
        </div>
      )}

      {DOC_CATEGORY_ORDER.map((cat) => {
        const docs = grouped[cat];
        if (!docs || docs.length === 0) return null;
        return (
          <div key={cat} style={S.docsGroup}>
            <div style={S.docsGroupTitle}>
              <span>{DOC_CATEGORY_ICONS[cat] || '📄'}</span>
              <span>{DOC_CATEGORY_LABELS[cat] || cat}</span>
              <span style={{ color: '#8fa8c0', fontWeight: 400, fontSize: 12 }}>({docs.length})</span>
            </div>
            {docs.map((d) => (
              <div key={d.id} style={S.docRow}>
                <a href={d.storage_url} target="_blank" rel="noreferrer"
                   style={{ color: '#e5edf5', textDecoration: 'none', flex: 1, minWidth: 0 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    <b>{d.file_name}</b>{d.file_type === 'cost_analysis' && <span style={{ marginLeft: 8, color: '#8fa8c0', fontSize: 11 }}>Cost Analysis</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#8fa8c0' }}>
                    {fmtSize(d.file_size_bytes)} · {d.source === 'sam_gov' ? '📡 SAM.gov' : '⬆️ Uploaded'} · {d.uploaded_at && format(parseISO(d.uploaded_at), 'MMM d, yyyy')}
                    {d.auto_categorized && d.category_confidence && (
                      <span style={{ marginLeft: 8, opacity: 0.7 }}>
                        (auto-tagged, {d.category_confidence})
                      </span>
                    )}
                  </div>
                </a>
                <select
                  style={{ ...S.select, fontSize: 11, padding: '4px 8px', width: 'auto' }}
                  value={d.category}
                  onChange={(e) => handleRecategorize(d.id, e.target.value)}
                >
                  {DOC_CATEGORY_ORDER.map((c) => (
                    <option key={c} value={c}>{DOC_CATEGORY_LABELS[c] || c}</option>
                  ))}
                </select>
                <button style={{ ...S.ghostBtn, padding: '4px 10px' }} onClick={() => handleDelete(d.id)}>🗑️</button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ─── SCHEDULE TAB ────────────────────────────────────────────────────────────
const ScheduleTab: React.FC<{
  solicitationId: string;
  events: Event[];
  hasResponseDeadline: boolean;
  onReload: () => void;
}> = ({ solicitationId, events, hasResponseDeadline, onReload }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({ kind: 'team_meeting', title: '', starts_at: '', ends_at: '', location: '' });

  const create = async () => {
    if (!form.title || !form.starts_at) { alert('Title and start time required'); return; }
    try {
      await api.post(`/gov/solicitations/${solicitationId}/events`, {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
      });
      setShowForm(false);
      setForm({ kind: 'team_meeting', title: '', starts_at: '', ends_at: '', location: '' });
      onReload();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const autoSchedule = async () => {
    try {
      await api.post(`/gov/solicitations/${solicitationId}/auto-schedule-deadline`);
      onReload();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const del = async (eventId: string) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/gov/events/${eventId}`);
      onReload();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.starts_at) >= now);
  const past = events.filter((e) => new Date(e.starts_at) < now);

  const kindLabel = (k: string) => ({
    response_deadline: '🚨 Response Deadline', qa_cutoff: '❓ Q&A Cutoff',
    site_visit: '🏗️ Site Visit', amendment_check: '🔄 Amendment Check',
    internal_review: '👥 Internal Review', draft_deadline: '📝 Draft Deadline',
    cost_review: '💰 Cost Review', team_meeting: '🤝 Team Meeting',
    bond_deadline: '🔒 Bond Deadline', award_expected: '🏆 Award Expected',
    follow_up: '📞 Follow-Up', other: '📌 Event',
  } as any)[k] || k;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button style={S.primaryBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Close' : '+ Schedule Event'}
        </button>
        {hasResponseDeadline && (
          <button style={S.ghostBtn} onClick={autoSchedule}>
            📅 Auto-add response deadline
          </button>
        )}
      </div>

      {showForm && (
        <div style={S.card}>
          <div style={S.cardTitle}>New Event</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <div>
              <div style={S.k}>Kind</div>
              <select style={S.select} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="team_meeting">🤝 Team Meeting</option>
                <option value="internal_review">👥 Internal Review</option>
                <option value="cost_review">💰 Cost Review</option>
                <option value="site_visit">🏗️ Site Visit</option>
                <option value="qa_cutoff">❓ Q&A Cutoff</option>
                <option value="draft_deadline">📝 Draft Deadline</option>
                <option value="bond_deadline">🔒 Bond Deadline</option>
                <option value="follow_up">📞 Follow-Up</option>
                <option value="amendment_check">🔄 Amendment Check</option>
                <option value="award_expected">🏆 Award Expected</option>
                <option value="other">📌 Other</option>
              </select>
            </div>
            <div>
              <div style={S.k}>Title</div>
              <input style={S.input} value={form.title} placeholder="Kickoff strategy meeting"
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <div style={S.k}>Start</div>
              <input style={S.input} type="datetime-local" value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
            </div>
            <div>
              <div style={S.k}>End (optional)</div>
              <input style={S.input} type="datetime-local" value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <div style={S.k}>Location</div>
              <input style={S.input} value={form.location} placeholder="Zoom / office / TBD"
                onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button style={S.primaryBtn} onClick={create}>Create</button>
            <button style={S.ghostBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={S.cardTitle}>Upcoming ({upcoming.length})</div>
        {upcoming.length === 0 && <div style={{ color: '#8fa8c0', fontSize: 13 }}>No upcoming events.</div>}
        {upcoming.map((ev) => {
          const days = differenceInCalendarDays(parseISO(ev.starts_at), new Date());
          return (
            <div key={ev.id} style={S.eventRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {kindLabel(ev.kind)} — {ev.title}
                </div>
                <div style={{ fontSize: 12, color: '#8fa8c0' }}>
                  {format(parseISO(ev.starts_at), 'EEE MMM d · h:mm a')}
                  {ev.location && ` · 📍 ${ev.location}`}
                  {ev.auto_generated && ' · auto-created'}
                </div>
              </div>
              <div style={{ ...S.deadlineChip, background: days < 3 ? '#ef4444' : days < 7 ? '#f97316' : '#4a7eb5' }}>
                {days === 0 ? 'TODAY' : `${days}d`}
              </div>
              <button style={{ ...S.ghostBtn, padding: '4px 10px' }} onClick={() => del(ev.id)}>🗑️</button>
            </div>
          );
        })}
      </div>

      {past.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>Past ({past.length})</div>
          {past.slice(-5).reverse().map((ev) => (
            <div key={ev.id} style={{ ...S.eventRow, opacity: 0.6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>{kindLabel(ev.kind)} — {ev.title}</div>
                <div style={{ fontSize: 11, color: '#8fa8c0' }}>{format(parseISO(ev.starts_at), 'MMM d, yyyy')}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ACTIVITY TAB ────────────────────────────────────────────────────────────
const ACTION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  created:               { label: 'Solicitation created',          icon: '✨', color: '#4a7eb5' },
  created_from_ss:       { label: 'Converted from Sources Sought', icon: '🔄', color: '#8b5cf6' },
  stage_advance:         { label: 'Stage advanced',                icon: '➡️', color: '#10b981' },
  checklist_toggle:      { label: 'Checklist item',                icon: '☑️', color: '#4a7eb5' },
  note:                  { label: 'Note added',                    icon: '📝', color: '#eab308' },
  document_upload:       { label: 'Document uploaded',             icon: '📎', color: '#4a7eb5' },
  event_created:         { label: 'Event scheduled',               icon: '📅', color: '#8b5cf6' },
  converted:             { label: 'Converted to next pipeline',    icon: '🚀', color: '#10b981' },
  metadata_refetched:    { label: 'Metadata refreshed',            icon: '📡', color: '#4a7eb5' },
};

function taskLabelForKey(stage: string, key: string): string {
  const pool = [
    ...(SOURCES_SOUGHT_TASKS[stage] || []),
    ...(ACTIVE_TASKS[stage] || []),
  ];
  const found = pool.find((t) => t.key === key);
  return found?.label || key.replace(/_/g, ' ');
}

const renderActivityDetails = (a: Activity): React.ReactNode => {
  const md = a.metadata || {};
  switch (a.action) {
    case 'checklist_toggle':
      if (md.stage && md.key) {
        return (
          <>
            <div style={{ fontSize: 13, color: '#e5edf5' }}>
              {md.checked ? '✅ ' : '⬜ '}<b>{taskLabelForKey(md.stage, md.key)}</b>
            </div>
            <div style={{ fontSize: 11, color: '#8fa8c0', marginTop: 2 }}>
              in stage <b>{STAGE_LABELS[md.stage] || md.stage}</b>
            </div>
          </>
        );
      }
      break;
    case 'stage_advance':
      if (a.from_stage && a.to_stage) {
        return (
          <div style={{ fontSize: 13, color: '#e5edf5' }}>
            <b>{STAGE_LABELS[a.from_stage] || a.from_stage}</b>
            {' → '}
            <b style={{ color: '#10b981' }}>{STAGE_LABELS[a.to_stage] || a.to_stage}</b>
          </div>
        );
      }
      break;
    case 'created':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>{a.message || 'New solicitation record'}</div>;
    case 'note':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>“{a.message}”</div>;
    case 'document_upload':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>{a.message || 'File uploaded'}</div>;
    case 'event_created':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>{a.message || 'Calendar event added'}</div>;
    case 'converted':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>Converted to next pipeline</div>;
    case 'metadata_refetched':
      return <div style={{ fontSize: 13, color: '#e5edf5' }}>{a.message || 'Metadata refreshed'}</div>;
  }
  if (a.message) return <div style={{ fontSize: 13, color: '#e5edf5' }}>{a.message}</div>;
  if (Object.keys(md).length) {
    return (
      <div style={{ fontSize: 11, color: '#8fa8c0', fontFamily: 'monospace' as const }}>
        {JSON.stringify(md)}
      </div>
    );
  }
  return null;
};

const ActivityTab: React.FC<{ activity: Activity[] }> = ({ activity }) => (
  <div style={S.card}>
    {activity.length === 0 && <div style={{ color: '#8fa8c0', fontSize: 13 }}>No activity yet.</div>}
    {activity.map((a) => {
      const meta = ACTION_LABELS[a.action] || {
        label: a.action.replace(/_/g, ' '), icon: '•', color: '#8fa8c0',
      };
      return (
        <div key={a.id} style={S.activityRow}>
          <div style={{ minWidth: 140, color: '#8fa8c0', fontSize: 12 }}>
            {format(parseISO(a.created_at), 'MMM d, h:mm a')}
          </div>
          <div style={{ fontSize: 18, lineHeight: 1, width: 24, textAlign: 'center' as const, flexShrink: 0 }}>{meta.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: meta.color, fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 3 }}>
              {meta.label}
              {a.actor_name && (
                <span style={{ color: '#8fa8c0', textTransform: 'none' as const, fontWeight: 400, marginLeft: 8 }}>
                  by {a.actor_name}
                </span>
              )}
            </div>
            {renderActivityDetails(a)}
          </div>
        </div>
      );
    })}
  </div>
);

export default GovSolicitationDetailPage;
